import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { Table, Popconfirm } from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useMutation, useQuery } from "@apollo/client";
import { faFilePdf } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Spinner from "@src/components/spinner";
import { getPayBillByScheduleIdQuery } from "@src/util/query/paybill.query";
import { getScheduledJobById } from "@src/util/query/schedule.query";
import { calculatePayBillMutation } from "@src/util/mutation/calculatePayBill.mutation";
import { setNotification } from '@src/middleware/redux/reducers/notification.reducer';
import { fixDecimal } from "@src/util/convertDecimalToTime/fixDecimal";
import { parseBreakDuration } from "@src/util/breakDuration/breakDuration";
import { currencySymbol } from "@src/util/currency/currency";
import { useDefaultCurrency } from "@src/hooks/useDefaultCurrency";
import { HelpLink } from "@src/components/helpLink/helpLink";

dayjs.extend(isoWeek);

export const EditDutyPayBillTab = ({scheduleId})=>{
    // pay & bill records calculated before currencies were recorded are in the tenant's default
    const defaultCurrency = useDefaultCurrency();

    const dispatch = useDispatch();

    const {data: paybillData, loading: paybillLoading, refetch: refetchPayBill} = useQuery(getPayBillByScheduleIdQuery,{
        variables:{
            scheduleId,
        },
        fetchPolicy:'network-only'
    });

    // Needed only to pick up the assigned employee's overtime policy and confirm the pay/bill
    // start-end times are set, same as the Times tab's own Recalculate button.
    const {data: scheduledJobData} = useQuery(getScheduledJobById,{
        variables: { jobId: scheduleId },
        fetchPolicy: 'network-only',
        skip: !scheduleId
    });
    const scheduleData = scheduledJobData?.getScheduledJobById?.response;

    const [calculatePayBill, {loading: calculatePayBillLoading}] = useMutation(calculatePayBillMutation);

    // See editDutyTimes.jsx's handelPayBill for why this widens scope by overtime policy rather
    // than always recalculating just this one duty.
    const handelPayBill = async()=>{
        try{
            const overtimePolicy = scheduleData?.assignedEmployeeDetail?.metadata?.overtimepolicy;
            const employeeId = scheduleData?.assignedEmployee;

            let input = { scheduleIds: [scheduleId] };
            let scopeNote = "";

            if(overtimePolicy && employeeId && scheduleData?.firstDate){
                const anchor = dayjs(scheduleData.firstDate);
                let rangeStart, rangeEnd, periodLabel;

                if(overtimePolicy === "Daily Overtime"){
                    rangeStart = anchor.startOf('day');
                    rangeEnd = anchor.endOf('day');
                    periodLabel = "day";
                }else if(overtimePolicy === "Weekly Overtime"){
                    rangeStart = anchor.startOf('isoWeek');
                    rangeEnd = anchor.endOf('isoWeek');
                    periodLabel = "week";
                }else if(overtimePolicy === "Monthly Overtime"){
                    rangeStart = anchor.startOf('month');
                    rangeEnd = anchor.endOf('month');
                    periodLabel = "month";
                }

                if(rangeStart && rangeEnd){
                    input = {
                        employeeIds: [employeeId],
                        startDate: rangeStart.format("DD/MM/YYYY"),
                        endDate: rangeEnd.format("DD/MM/YYYY"),
                    };
                    scopeNote = ` (recalculated the employee's whole ${periodLabel} to keep overtime correct)`;
                }
            }

            await calculatePayBill({ variables: { input } });
            await refetchPayBill();

            dispatch(setNotification({
                error: false,
                notificationState: true,
                message: `Pay & Bill was calculated successfully${scopeNote}`
            }));
        }catch(err){
            dispatch(setNotification({
                error: true,
                notificationState: true,
                message: "An error encountered while calculating pay/bill"
            }));
        }
    };

    useEffect(()=>{
        refetchPayBill();
    },[]);

    const columns = [
        {title: 'Type', dataIndex:'type', 
            // render: (text, row, index) => mergeRows(text, row, index),
        },
        {title: 'Start', dataIndex:'start'},
        {title: 'Finish', dataIndex:'finish'},
        {title: 'Hour Type', dataIndex:'hourType'},
        {title: 'Premium', dataIndex:'premium'},
        {title: 'Hours', dataIndex:'hour'},
        {title: 'Rate', dataIndex:'rate'},
        {title: 'Rate Applied', dataIndex:'rateapply'},
        {title: 'Break', dataIndex:'break'},
        {title: 'Ext Value', dataIndex:'ext'},
    ];


    // Function to split dates
    function splitDates(startDate, endDate) {
        if (startDate.isSame(endDate, 'day')) {
        // If the dates are on the same day, return only the original dates
        return [{ start: startDate.format(), end: endDate.format() }];
        } else {
        // Split into 4 parts if the dates are on different days
        const endOfFirstDay = startDate.endOf('day');
        const startOfSecondDay = endDate.startOf('day');
    
        return [
            { start: startDate.format(), end: endOfFirstDay.format() },       // Original first date to end of that day
            { start: startOfSecondDay.format(), end: endDate.format() }        // Start of second day to original second date
        ];
        }
    }

    const [dataSource, setDataSource] = useState();

    const HolidayColumns = [

        "65ee50966f6d12d9e20b21fe", // Bank Holiday Pay
        "65ff5f56cdef94de7cabc3e7" // Bank Holiday Bill
    ];

    // Total rows show the hours actually paid / billed - the amounts already have the break taken off
    const netOfBreak = (hours, breakDuration) => Math.max(0, (parseFloat(hours) || 0) - parseBreakDuration(breakDuration) / 60);

    const convertToHrsMin = (decimalHours) => {
        const hours = Math.floor(decimalHours); // Extract hours
        const minutes = Math.round((decimalHours - hours) * 60); // Convert decimal to minutes

        // Format hours and minutes with leading zero
        const formattedHours = String(hours).padStart(2, "0");
        const formattedMinutes = String(minutes).padStart(2, "0");

        return `${formattedHours} hrs ${formattedMinutes} m`;
    }

    // premium rows match the duty rows: money to 2 decimals, hours as "12 hrs 00 m" (none for a per-shift premium)
    const premiumMoney = (sym, value) => sym+" "+(parseFloat(value) || 0).toFixed(2);
    const premiumHours = (hours) => hours==null || hours==="" ? "" : convertToHrsMin(fixDecimal(hours, 2));

    // Clocks-change confirmation row - dstChange is populated by calculatePayBill whenever a
    // duty's pay or bill period crosses a UK BST/GMT change. The hour rows above already
    // include the adjustment; this row just confirms that plainly instead of leaving a 7 or 9
    // hour duty looking unexplained.
    const dstChangeRow = (dst) => {
        if(!dst) return null;
        const sign = dst.direction === 'gained' ? '+' : '-';
        const note = dst.direction === 'gained' ? 'Clocks went back (BST → GMT)' : 'Clocks went forward (GMT → BST)';
        return {
            type: <div style={{fontWeight:700, color:'#fd7e14'}}>Clock Change (Day time saving)</div>,
            hourType: note,
            hour: <div style={{color:'#fd7e14', fontWeight:700}}>{sign}{dst.hours} hr</div>,
            rateapply: 'Hours above already include this adjustment',
        };
    };

    useEffect(()=>{
        if(paybillData?.getPayBill?.response){
            const paybill = paybillData?.getPayBill?.response;
            const paybillDetail = paybill?.paybillDetail
            // each side in the currency it was calculated in
            const paySym  = currencySymbol(paybill?.payCurrency  || defaultCurrency);
            const billSym = currencySymbol(paybill?.billCurrency || defaultCurrency);
            const payDetail = paybill?.paybillDetail?.payDetail;
            const billDetail = paybill?.paybillDetail?.billDetail;


            let payPremiumOnly = paybill?.payBillPremium.flatMap(subArray => 
                subArray.filter(item => item.hasOwnProperty('payPremium'))
            );

            let billPremiumOnly = paybill?.payBillPremium.flatMap(subArray => 
                subArray.filter(item => item.hasOwnProperty('billPremium'))
            );

            console.log(paybill, "payDetailSafras");
            
            const payDates = splitDates(dayjs(payDetail?.payStartDateTime), dayjs(payDetail?.payEndDateTime));
            let pay = [
                {
                    type: <div style={{fontWeight:800}}>Pay</div>,
                    start: dayjs(payDates[0]?.start).local().format("DD/MM/YYYY HH:mm"),
                    finish: dayjs(payDates[0]?.end).local().format("DD/MM/YYYY HH:mm"),
                    hourType: <span>
                        {payDetail?.name}
                        <small>{(paybillDetail?.isPayFirstDateInBankHoilday==true &&  !HolidayColumns.includes(payDetail?.payColumn)) &&  "( In BH )"}</small>
                    </span>  ,
                    hour: convertToHrsMin(fixDecimal(paybill?.payFirstDateHour, 2)),
                    rate: paySym+" "+parseFloat(paybillDetail?.payRate?.firstDatePayRate).toFixed(2),
                    rateapply: paybillDetail?.payRateDescription,
                    ext: paySym+" "+parseFloat(paybill?.firstDatePay).toFixed(2),
                    break: parseBreakDuration(paybillDetail?.unPaidBreakDuration) > 0 ?
                        <span>

                        {parseFloat(paybill?.payFirstDateHour)>0? "- "+convertToHrsMin(parseBreakDuration(paybillDetail?.unPaidBreakDuration)/60)  :"" }
                        <br/>
                        {parseFloat(paybill?.payFirstDateHour)>0? "- "+paySym+" "+ parseFloat(paybill?.unpaidBreakAmount).toFixed(2) :"" }

                    </span> : ""
                },

            ];
            if(payDates && payDates?.length>0){
                if(payDates[1]){
                    pay.push({
                        type: 'Pay',
                        start: dayjs(payDates[1]?.start).local().format("DD/MM/YYYY HH:mm"),
                        finish: dayjs(payDates[1]?.end).local().format("DD/MM/YYYY HH:mm"),
                        hourType:  <span>
                            {payDetail?.name}
                            <small>{(paybillDetail?.isPaySecondDateInBankHoilday==true &&  !HolidayColumns.includes(payDetail?.payColumn)) &&  "( In BH )"}</small>
                        </span>,
                        hour: convertToHrsMin(fixDecimal(paybill?.paySecondDateHour, 2)),
                        rate: paySym+" "+parseFloat(paybillDetail?.payRate?.secondDatePayRate).toFixed(2),
                        rateapply: paybillDetail?.payRateDescription,
                        ext: paySym+" "+parseFloat(paybill?.secondDatePay).toFixed(2)
                    });
                }
            }

            if(paybillDetail?.payOverTimeHour && paybillDetail?.payOverTimeHour>0){
                pay.push(
                    {
                        type: <div style={{fontWeight:800}}>Additional / Overtime Pay</div>,
                        
                        hour: convertToHrsMin(fixDecimal(paybill?.payOverTimeHour, 2)),
                        rate: paySym+" "+parseFloat(paybill?.overTimePayRate).toFixed(2),

                        ext: paySym+" "+parseFloat(paybill?.overTimePay, 2).toFixed(2),
                        break:parseBreakDuration(paybillDetail?.unPaidBreakDuration) > 0 ? <span>

                            {parseFloat(paybill?.payFirstDateHour)==0? "- "+convertToHrsMin(parseBreakDuration(paybillDetail?.unPaidBreakDuration)/60)  :"" }
                                <br/>
                            {parseFloat(paybill?.payFirstDateHour)==0? "- "+paySym+" "+ parseFloat(paybill?.unpaidBreakAmount).toFixed(2) :"" }

                        </span> : ""
                    }
                );
            }

            if(payPremiumOnly && payPremiumOnly?.length>0){
                payPremiumOnly?.map((pp)=>{
                    const {payPremium: premia} = pp;
                    pay.push({
                        type: "",
                        start: "",
                        finish: "",
                        hourType: "",
                        premium: premia?.premiumName,
                        hour: premiumHours(premia?.totalPremiumHour),
                        rate: premiumMoney(paySym, premia?.premiumRate),
                        ext: premia?.rejected || premiumMoney(paySym, premia?.totalPremiumPay)
                    });
                })
            }

            const payDstRow = dstChangeRow(paybill?.dstChange?.pay);
            if(payDstRow){
                pay.push(payDstRow);
            }

            pay.push({
                type: <div style={{padding:'20px 0', fontSize:'14px', color:'#dc3545', fontWeight:'800'}}>Total Pay</div>,
                start: "",
                finish: "",
                hourType: "",
                hour: <div style={{padding:'20px 0', fontSize:'14px', color:'#dc3545', fontWeight:'800'}}>{convertToHrsMin(fixDecimal(netOfBreak(paybillDetail?.totalPayHour, paybillDetail?.unPaidBreakDuration), 2))}</div>,
                rate: "",
                ext: <div style={{padding:'20px 0', fontSize:'14px', color:'#dc3545 ', fontWeight:'800'}}>{paySym} {parseFloat(paybill?.totalPay).toFixed(2)} </div>
            });

            const billDates = splitDates(dayjs(billDetail?.billStartDateTime), dayjs(billDetail?.billEndDateTime));
            let bill = [
                {
                    type: <div style={{fontWeight:800}}>Bill</div>,
                    start: dayjs(billDates[0]?.start).local().format("DD/MM/YYYY HH:mm"),
                    finish: dayjs(billDates[0]?.end).local().format("DD/MM/YYYY HH:mm"),
                    hourType: <span>
                        {billDetail?.name}
                        <small>{(paybillDetail?.isBillFirstDateInBankHoilday==true &&  !HolidayColumns.includes(payDetail?.billColumn)) &&  "( In BH )"}</small>
                    </span>,
                    hour: convertToHrsMin(fixDecimal(paybill?.billFirstDateHour, 2)),
                    rate: billSym+" "+parseFloat(paybillDetail?.billRate?.firstDateBillRate).toFixed(2),
                    rateapply: paybillDetail?.billRateDescription,
                    ext: billSym+" "+ parseFloat(paybill?.firstDateBill).toFixed(2),
                    break:parseBreakDuration(paybillDetail?.unBillBreakDuration) > 0 ?
                      <span>

                        {parseFloat(paybill?.billFirstDateHour)>0? "- "+convertToHrsMin(parseBreakDuration(paybillDetail?.unBillBreakDuration)/60)  :"" }
                        <br/>
                        {parseFloat(paybill?.billFirstDateHour)>0? "- "+billSym+" "+ parseFloat(paybill?.unBillBreakAmount).toFixed(2) :"" }

                    </span> : ""
                },

            ];


            if(billDates && billDates?.length>0){
                if(billDates[1]){
                    bill.push({
                        type: 'Bill',
                        start: dayjs(billDates[1]?.start).local().format("DD/MM/YYYY HH:mm"),
                        finish: dayjs(billDates[1]?.end).local().format("DD/MM/YYYY HH:mm"),
                        hourType:  <span>
                            {billDetail?.name}
                            <small>{(paybillDetail?.isBillSecondDateInBankHoilday==true &&  !HolidayColumns.includes(payDetail?.billColumn)) &&  "( In BH )"}</small>
                        </span>,
                        hour: convertToHrsMin(fixDecimal(paybill?.billSecondDateHour,2)),
                        rate: billSym+" "+parseFloat(paybillDetail?.billRate?.secondDateBillRate).toFixed(2),
                        rateapply: paybillDetail?.billRateDescription,
                        ext: billSym+" "+parseFloat(paybill?.secondDateBill).toFixed(2),

                    });
                }
            }


            if(paybillDetail?.billOverTimeHour && paybillDetail?.billOverTimeHour>0){
                bill.push(
                    {
                        type: <div style={{fontWeight:800}}>Additional / Overtime Bill</div>,
                        hour: convertToHrsMin(fixDecimal(paybill?.billOverTimeHour, 2)),
                        rate: billSym+" "+fixDecimal(paybillDetail?.overTimeBillRate,2),
                        ext: billSym+" "+fixDecimal(paybill?.overTimeBill, 2),
                        break:parseBreakDuration(paybillDetail?.unBillBreakDuration) > 0 ? <span>

                            {parseFloat(paybill?.billFirstDateHour)==0? "- "+convertToHrsMin(parseBreakDuration(paybillDetail?.unBillBreakDuration)/60)  :"" }
                                <br/>
                            {parseFloat(paybill?.billFirstDateHour)==0? "- "+billSym+" "+ parseFloat(paybill?.unBillBreakAmount).toFixed(2) :"" }

                        </span> : ""
                    }
                );
            }

            if(billPremiumOnly && billPremiumOnly?.length>0){
                billPremiumOnly?.map((pp)=>{
                    const {billPremium: premia} = pp;
                    bill.push({
                        type: "",
                        start: "",
                        finish: "",
                        hourType: "",
                        premium: premia?.premiumName,
                        hour: premiumHours(premia?.totalPremiumHour),
                        rate: premiumMoney(billSym, premia?.premiumRate),
                        ext: premia?.rejected || premiumMoney(billSym, premia?.totalPremiumBill)
                    });
                })
            }

            const billDstRow = dstChangeRow(paybill?.dstChange?.bill);
            if(billDstRow){
                bill.push(billDstRow);
            }

            bill.push({
                type: <div style={{padding:'20px 0', fontSize:'14px', color:'#28a745', fontWeight:'800'}}>Total Bill</div>,
                start: "",
                finish: "",
                hourType: "",
                hour: <div style={{padding:'20px 0', fontSize:'14px', color:'#28a745', fontWeight:'800'}}>{ convertToHrsMin(fixDecimal(netOfBreak(paybillDetail?.totalBillHour, paybillDetail?.unBillBreakDuration), 2)) }</div>,
                rate: "",
                ext: <div style={{padding:'20px 0', fontSize:'14px', color:'#28a745', fontWeight:'800'}}>{billSym} {parseFloat(paybill?.totalBill).toFixed(2)} </div>,
            })

            setDataSource([...pay, ...bill])
        }
    }, [paybillData]);

    return(
          
        <div className="box-shadow border-radius" style={{overflow:'auto', height:'75vh'}}>

            <div style={{padding:'16px'}}>
                    <div className='hr-info-title' style={{display:'flex', justifyContent:'space-between'}}>
                        <span>Pay & Bill &nbsp;<HelpLink slug="pay-and-bill">How pay & bill is worked out</HelpLink></span>
                        {/* <button className="drawer-outlined-btn" style={{padding:'5px 10px'}}> <FontAwesomeIcon icon={faFilePdf}/> Export Pdf</button> */}
                        {(()=>{
                            const payBillReady = scheduleData?.payStartDateTime && scheduleData?.payEndDateTime
                                && scheduleData?.billStartDateTime && scheduleData?.billEndDateTime;
                            const overtimePolicy = scheduleData?.assignedEmployeeDetail?.metadata?.overtimepolicy;
                            const scopeHint = overtimePolicy === "Daily Overtime" ? "This will recalculate the employee's whole day, to keep overtime correct."
                                : overtimePolicy === "Weekly Overtime" ? "This will recalculate the employee's whole week, to keep overtime correct."
                                : overtimePolicy === "Monthly Overtime" ? "This will recalculate the employee's whole month, to keep overtime correct."
                                : "This employee has no overtime policy set - only this duty will be recalculated.";
                            const btnLabel = paybillData?.getPayBill?.response ? "Recalculate Pay & Bill" : "Calculate Pay & Bill";
                            return (
                                <Popconfirm
                                    title={btnLabel}
                                    description={scopeHint}
                                    onConfirm={handelPayBill}
                                    okText="Proceed"
                                    cancelText="Cancel"
                                    disabled={calculatePayBillLoading || !payBillReady}
                                    getPopupContainer={(triggerNode)=> triggerNode.parentElement || document.body}
                                >
                                    <button
                                        disabled={calculatePayBillLoading || !payBillReady}
                                        className={(calculatePayBillLoading || !payBillReady) ? "drawer-outlined-btn disabled-btn" : "drawer-outlined-btn"}
                                        style={{padding:'5px 10px'}}
                                        title={!payBillReady ? "Pay and bill start/end times must be set before calculating" : undefined}
                                    >
                                        {calculatePayBillLoading? <Spinner/> : btnLabel}
                                    </button>
                                </Popconfirm>
                            );
                        })()}
                    </div>
            </div>

            <Table
                className="curvedTable"
                style={{fontWeight:'400'}}
                columns={columns}
                dataSource={dataSource}
                locale={{emptyText:'Pay/Bill not calculated yet'}}
                loading={paybillLoading}
                pagination={false}
            />


        </div>
    );
}